import { useState, type ReactElement } from 'react';
import type { PlanDto } from '@gr/core/accounts/plans/dto';
import { useConfig, useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { SelectField } from '../../../ui-kit/Field/Field';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PlanUsage } from './PlanUsage/PlanUsage';

export interface UserPlanDialogProps {
  user: { id: string; login: string };
  /** Планы для выбора: архивные предлагаются, только если пользователь уже на таком. */
  plans: readonly PlanDto[];
  onClose(): void;
  /** План назначен — обновить столбец «Тариф». */
  onAssigned(planId: string): void;
}

// PLAN-UC-4, PLAN-RULE-3, PLAN-RULE-5, DEMO-RULE-3: план пользователя с израсходованным и назначение другого.
export function UserPlanDialog({ user, plans, onClose, onAssigned }: UserPlanDialogProps): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const readOnly = useConfig().demo.enabled;
  const account = useResource(() => api.plans.userPlan(user.id), [api, user.id]);
  const [planId, setPlanId] = useState<string | null>(null);
  const assign = useAction((id: string) => api.plans.assign(user.id, id));
  const current = account.data?.plan.id ?? '';
  const selected = planId ?? current;
  const options = plans
    .filter((plan) => !plan.archived || plan.id === current)
    .map((plan) => ({ value: plan.id, label: plan.archived ? t('plans.user.archivedOption', { name: plan.name }) : plan.name }));

  const save = async (): Promise<void> => {
    const result = await assign.run(selected);
    if (result.success && result.data) {
      account.setData(result.data);
      setPlanId(null);
      onAssigned(selected);
    }
  };

  return (
    <Dialog
      open
      title={t('plans.user.title', { login: user.login })}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.close')}</Button>
          {readOnly ? null : (
            <Button variant="primary" loading={assign.pending} disabled={!account.data || selected === current} onClick={() => void save()}>
              {t('plans.user.save')}
            </Button>
          )}
        </>
      }
    >
      {account.loading && !account.data ? (
        <Loading label={t('common.loading')} />
      ) : account.error || !account.data ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          {te(account.error)}
        </Alert>
      ) : (
        <div className="u-stack">
          <SelectField label={t('plans.user.select')} value={selected} options={options} disabled={readOnly || assign.pending} onChange={(event) => setPlanId(event.target.value)} />
          {assign.error ? <Alert tone="danger">{te(assign.error)}</Alert> : null}
          <PlanUsage account={account.data} />
        </div>
      )}
    </Dialog>
  );
}
